import React, { useState, useEffect } from 'react'
import { useAppSelector, useAppDispatch } from 'app/hooks'
import { RootState, PromptType, UserRole } from 'app/types'
import { Button, Spinner, DropdownMenu, PreviewHeader, IconButton } from 'components'
import { navigate, showPrompt } from 'actions/appActions'
import { cloneTemplateToContext } from 'actions/adminActions'
import { DynamicPage } from 'views/DynamicPage'
import { MemoryRouter } from 'react-router-dom'
import { logger } from 'helpers/logger'

interface CloneTemplateSelectorProps {
    templatePage: any
    sourceApp?: any
    sourceOrganization?: any
    onClose: () => void
    onCloneSuccess?: (result: any) => void
    onNavigate?: (page: any, options?: { targetApp?: any, targetOrganization?: any, reload?: boolean }) => void
}

export function CloneTemplateSelector({ templatePage, sourceApp, sourceOrganization, onClose, onCloneSuccess, onNavigate }: CloneTemplateSelectorProps) {
    const dispatch = useAppDispatch()

    let user = useAppSelector((state: RootState) => state.user.data)
    if (Object.keys(user).length === 0) user = null
    const config = useAppSelector((state: RootState) => state.app.config)
    const organization = useAppSelector((state: RootState) => state.app.organization)
    const standaloneOrg = useAppSelector((state: RootState) => state.app.standaloneOrg)
    const currentProject = useAppSelector((state: RootState) => state.app.projectCurrent[0])
    const standaloneApp = useAppSelector((state: RootState) => state.app.standaloneApp)
    const customDomain = useAppSelector((state: RootState) => state.app.customDomain)

    const [cloning, setCloning] = useState(false)
    const [userApps, setUserApps] = useState([])
    const [userOrganizations, setUserOrganizations] = useState([])
    const [selectedAppId, setSelectedAppId] = useState('')
    const [selectedOrganizationId, setSelectedOrganizationId] = useState('')
    const [cloneTarget, setCloneTarget] = useState('app')

    // Permission helper functions
    const hasOrganizationsEditRole = () => {
        return user?.roles && user.roles.includes(UserRole.OrganizationsEdit)
    }

    const canCloneToMultipleOrganizations = () => {
        return hasOrganizationsEditRole()
    }

    const getAvailableOrganizations = () => {
        if (!user) return []

        if (canCloneToMultipleOrganizations()) {
            if (config?.organizations && Array.isArray(config.organizations)) {
                return config.organizations.filter(org => org.id && org.name)
            }
        } else {
            if (organization && organization.id && organization.id == user.organizationId) {
                return [organization]
            }
        }

        return []
    }

    // Initialize data when component mounts
    useEffect(() => {
        // Handle user apps
        if (user?.apps && Array.isArray(user.apps)) {
            const validApps = user.apps.filter(app =>
                (app.id || app.meta?.id) && (app.name || app.meta?.name)
            )

            setUserApps(validApps)

            // Default to current project if it exists in the user's apps list
            let defaultAppId = null
            if (currentProject?.meta?.id) {
                const currentProjectInList = validApps.find(app =>
                    (app.id || app.meta?.id) === currentProject.meta.id
                )
                if (currentProjectInList) {
                    defaultAppId = currentProjectInList.id || currentProjectInList.meta?.id
                }
            }

            // Fallback to first app if current project not found in list
            if (!defaultAppId && validApps.length > 0) {
                defaultAppId = validApps[0].id || validApps[0].meta?.id
            }

            setSelectedAppId(defaultAppId || '')
        } else {
            setUserApps([])
            setSelectedAppId('')
        }

        // Handle user organizations with permission checks
        const availableOrganizations = getAvailableOrganizations()
        setUserOrganizations(availableOrganizations)

        // Default to current organization if available
        let defaultOrgId = null
        if (organization?.id) {
            const currentOrgInList = availableOrganizations.find(org => org.id === organization.id)
            if (currentOrgInList) {
                defaultOrgId = currentOrgInList.id
            }
        }

        // Fallback to first available organization
        if (!defaultOrgId && availableOrganizations.length > 0) {
            defaultOrgId = availableOrganizations[0].id
        }

        setSelectedOrganizationId(defaultOrgId || '')

        // Set default clone target based on source data
        if (sourceOrganization && !sourceApp && availableOrganizations.length > 0) {
            setCloneTarget('organization')
        } else if (sourceApp && user?.apps && user.apps.length > 0) {
            setCloneTarget('app')
        } else if (user?.apps && user.apps.length > 0) {
            setCloneTarget('app')
        } else if (availableOrganizations.length > 0) {
            setCloneTarget('organization')
        }
    }, [user, currentProject, organization, config, sourceApp, sourceOrganization])

    async function handleCloneTemplate() {
        if (!templatePage) return

        setCloning(true)
        try {
            let result
            const proms = []
            proms.push(dispatch(showPrompt({
                title: 'Cloning Template',
                message: 'Please wait while we clone the template page...',
                successMessage: 'Template cloned successfully! You will now be taken to your new page.',
                type: PromptType.Pending,
            })))

            if (cloneTarget === 'app') {
                if (!selectedAppId) {
                    dispatch(showPrompt({
                        title: 'No App Selected',
                        message: 'Please select an app to add the template page to.',
                        type: PromptType.Confirm,
                        cancelMessage: null,
                        confirmMessage: 'OK'
                    }))
                    return
                }

                proms.push(dispatch(cloneTemplateToContext({
                    pageId: templatePage.id,
                    targetContext: { type: 'app', id: selectedAppId }
                })))
            } else {
                if (!selectedOrganizationId) {
                    dispatch(showPrompt({
                        title: 'No Organization Selected',
                        message: 'Please select an organization to add the template page to.',
                        type: PromptType.Confirm,
                        cancelMessage: null,
                        confirmMessage: 'OK'
                    }))
                    return
                }

                proms.push(dispatch(cloneTemplateToContext({
                    pageId: templatePage.id,
                    targetContext: { type: 'organization', id: selectedOrganizationId }
                })))
            }

            const results = await Promise.all(proms)
            result = results[results.length - 1]

            // Close the clone selector
            onClose()

            // Call success callback if provided
            if (onCloneSuccess) {
                onCloneSuccess(result)
            }

            // Navigate based on clone target
            if (onNavigate) {
                if (cloneTarget === 'app' && result.payload?.app && result.payload?.page) {
                    onNavigate(result.payload.page, { targetApp: result.payload.app, reload: false })
                } else if (cloneTarget === 'organization' && result.payload?.organization && result.payload?.page) {
                    onNavigate(result.payload.page, { targetOrganization: result.payload.organization, reload: false })
                }
            } else {
                if (cloneTarget === 'app' && result.payload?.app && result.payload?.page) {
                    const newApp = result.payload.app
                    const newAppLink = newApp.meta?.link || newApp.link
                    const newPageLink = result.payload.page.link
                    const isDifferentApp = !currentProject || (newApp.id !== (currentProject.id || currentProject.meta?.id))

                    let finalLink = ''
                    if(organization && window.location.pathname.includes(`/${organization.link}/`)) {
                        finalLink = `/${organization.link}`
                    } 
                    if(isDifferentApp || (newApp && window.location.pathname.includes(`/${newAppLink}/`))) {
                        finalLink += `/${newAppLink}`
                    }
                    finalLink += `/${newPageLink}`

                    if (isDifferentApp) {
                        window.location.reload()
                    }
                } else if (cloneTarget === 'organization' && result.payload?.organization && result.payload?.page) {
                    const newOrg = result.payload.organization
                    const newPage = result.payload.page
                    const orgLink = newOrg.link || newOrg.name?.toLowerCase().replace(/\s+/g, '-')
                    const pageLink = newPage.link
                    const isDifferentOrganization = !organization || (newOrg.id !== organization.id)

                    // For organization cloning, always include the organization link
                    if (orgLink && pageLink) {
                        let navUrl = ''
                        if(organization && organization.link == orgLink && standaloneOrg) {
                            navUrl = `/${pageLink}`
                        } else {
                            navUrl = `/${orgLink}/${pageLink}`
                        }
                        dispatch(navigate(navUrl))
                    } else {
                        const navUrl = `/${orgLink}`
                        dispatch(navigate(navUrl))
                    }

                    if (isDifferentOrganization) {
                        window.location.reload()
                    }
                }
            }

        } catch (error) {
            logger.error('Failed to clone template:', error)
            dispatch(showPrompt({
                title: 'Error',
                message: 'Failed to clone template page. Please try again.',
                type: PromptType.Confirm,
                cancelMessage: null,
                confirmMessage: 'OK'
            }))
        } finally {
            setCloning(false)
        }
    }

    if (!templatePage || !user) {
        return null
    }

    const appOptions = userApps.map(app => ({
        value: app.id || app.meta?.id,
        text: app.name || app.meta?.name || 'Unnamed Project'
    }))

    const organizationOptions = userOrganizations.map(org => ({
        value: org.id,
        text: org.name || 'Unnamed Organization'
    }))

    const hasApps = userApps.length > 0
    const hasOrganizations = userOrganizations.length > 0

    function getCloneForm() {
        return (
            <div className="template-selector scrollable">
                <div className="content-editor-topbar">
                    <IconButton
                        noBg
                        icon="fas fa-arrow-left"
                        onClick={onClose}
                        title="Close clone template"
                    />
                    <div className="content-editor-title">
                        <h4>Copy Page</h4>
                    </div>
                </div>

                <div className="clone-options">
                    <div className="target-info" style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                            Source {templatePage.pageType == 'blog' ? 'Blog' : 'Page'}
                        </label>
                        <span>{templatePage.name}</span>
                        {templatePage.helperText && (
                            <span className="template-description">{templatePage.helperText}</span>
                        )}
                    </div>
                    {!hasApps && !hasOrganizations ? (null
                        // <div className="no-targets-message" style={{
                        //     // backgroundColor: '#fff3cd',
                        //     padding: '20px',
                        //     borderRadius: '8px',
                        //     // border: '1px solid #ffeaa7',
                        //     textAlign: 'center'
                        // }}>
                        //     <h4>No Copy Targets Available</h4>
                        //     <p>You don't have access to any projects or organizations where you can clone this template page.</p>
                        // </div>
                    ) : (
                        <>
                            {/* Clone Target Selection */}
                            {hasApps && hasOrganizations && (
                                <div className="target-type-selection" style={{ marginBottom: '20px' }}>
                                    <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                                        Copy To
                                    </label>
                                    <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
                                        <label style={{ display: 'flex', alignItems: 'center' }}>
                                            <input
                                                type="radio"
                                                name="cloneTarget"
                                                value="app"
                                                checked={cloneTarget === 'app'}
                                                onChange={() => setCloneTarget('app')}
                                                style={{ marginRight: '8px' }}
                                            />
                                            Project
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center' }}>
                                            <input
                                                type="radio"
                                                name="cloneTarget"
                                                value="organization"
                                                checked={cloneTarget === 'organization'}
                                                onChange={() => setCloneTarget('organization')}
                                                style={{ marginRight: '8px' }}
                                            />
                                            Organization
                                        </label>
                                    </div>
                                </div>
                            )}

                            {/* App Selection */}
                            {cloneTarget === 'app' && hasApps && (
                                <div className="app-selector">
                                    <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                                        Select Project
                                    </label>
                                    <DropdownMenu
                                        value={selectedAppId}
                                        onChange={(value) => setSelectedAppId(value)}
                                        items={appOptions}
                                        placeholder="Select a project"
                                    />
                                </div>
                            )}

                            {/* Organization Selection */}
                            {cloneTarget === 'organization' && hasOrganizations && (
                                <div className="organization-selector" style={{ marginBottom: '20px' }}>
                                    <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                                        Select Organization
                                    </label>
                                    <DropdownMenu
                                        value={selectedOrganizationId}
                                        onChange={(value) => setSelectedOrganizationId(value)}
                                        items={organizationOptions}
                                        placeholder="Select an organization"
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="clone-actions" style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                    <Button onClick={onClose} secondary disabled={cloning}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCloneTemplate}
                        tertiary
                        disabled={
                            cloning ||
                            (!hasApps && !hasOrganizations) ||
                            (cloneTarget === 'app' && !selectedAppId) ||
                            (cloneTarget === 'organization' && !selectedOrganizationId)
                        }
                        icon="fas fa-check"
                    >
                        {cloning ? 'Copying...' : `Copy to ${cloneTarget === 'app' ? 'Project' : 'Organization'}`}
                    </Button>
                </div>
            </div>
        )
    }

    function getClonePreview() {
        if (!templatePage) {
            return (
                <div className="template-preview-empty" style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    padding: '60px 40px',
                    textAlign: 'center',
                    backgroundColor: '#f8f9fa'
                }}>
                    <h4>No Template Selected</h4>
                    <p>Template information is not available.</p>
                </div>
            )
        }

        return (
            <div className="template-preview">
                <PreviewHeader title={`Preview: ${templatePage.name}`} />

                <div className="template-preview-container" style={{
                    height: 'calc(100% - 80px)',
                    overflow: 'hidden',
                    position: 'relative'
                }}>
                    <div className="template-preview-scale-wrapper" style={{
                        transform: 'scale(0.8)',
                        transformOrigin: 'top left',
                        width: '125%',
                        height: '125%'
                    }}>
                        {(sourceApp || sourceOrganization) ? (
                            <MemoryRouter>
                                <DynamicPage
                                    app={sourceApp}
                                    organization={sourceOrganization}
                                    pageLink={templatePage.link}
                                    splitIndex={0}
                                    breadCrumbs={false}
                                    previewMode={true}
                                />
                            </MemoryRouter>
                        ) : (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                color: '#666',
                                textAlign: 'center',
                                padding: '20px'
                            }}>
                                <div>
                                    <i className="fas fa-image" style={{ fontSize: '48px', marginBottom: '15px', color: '#ddd' }}></i>
                                    <h4 style={{ margin: '0 0 10px 0', color: '#999' }}>Preview Unavailable</h4>
                                    <p style={{ margin: 0, fontSize: '14px' }}>
                                        Template preview cannot be loaded.<br />
                                        The page will be cloned with all its content intact.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="inline-content-editor template-mode fullscreen" style={{ zIndex: 100001 }}>
            {cloning && <Spinner invert showAfter={0} />}
            <div className="template-mode-container">
                <div className="template-mode-left content-editor">
                    {getCloneForm()}
                </div>
                <div className="template-mode-right">
                    {getClonePreview()}
                </div>
            </div>
        </div>
    )
}